/**
 * Generic settings rows, shared by the widget dialog and the wallpaper panel.
 *
 * Only the ROW lives here. Chrome — modal vs floating panel, footer buttons,
 * the widget escape hatch, `type:"size"` (which is host-owned and writes
 * cols/rows on a placement rather than into the values bag) — belongs to the
 * caller. That is the seam the widget dialog already had internally; this file
 * just makes it reusable.
 */
import type { SettingSpec } from "@arclog/module-sdk";
import { uiApi } from "./api";

export type RowHandle = { el: HTMLElement; destroy(): void };

export type BuildRowOptions = {
  spec: SettingSpec;
  value: unknown;
  /** CSS prefix — "os-wsd" for the widget dialog, "wsp" for the wallpaper panel. */
  prefix: string;
  onChange: (value: unknown) => void;
  /**
   * Fires when the user finishes interacting (pointerup / blur) rather than
   * on every step. The wallpaper panel uses this for `reload` keys: a slider's
   * onChange fires once per pixel of drag, so remounting on it would rebuild
   * the wallpaper hundreds of times across a single gesture.
   */
  onCommit?: (value: unknown) => void;
};

/**
 * Returns null for a spec this renderer does not handle — an unknown `type`
 * from a newer SDK, `type:"size"` (host-owned, built by the widget dialog
 * itself), or `type:"button"` (WALLPAPERS ONLY, host-owned, built by the
 * wallpaper panel itself — a widget declaring one gets nothing back here,
 * which is exactly the "widgets ignore it" contract).
 */
export function buildSettingRow(
  opts: BuildRowOptions
): { row: HTMLElement; handle: RowHandle } | null {
  const { spec: s, value: v, prefix, onChange } = opts;
  if (s.type === "size") return null;
  if (s.type === "button") return null;

  let handle: RowHandle;
  switch (s.type) {
    /* Every branch seeds a type-appropriate zero when the resolved value is
       undefined. This is NOT cosmetic: an unset <input type=range> reports its
       MIDPOINT and an unset <select> silently selects its first option, so
       getAll() would say `undefined` while the visible control said 50 — the
       form and the module would disagree about the same setting. */
    case "slider": {
      const min = s.min ?? 0;
      handle = uiApi.slider({
        min,
        max: s.max ?? 100,
        step: s.step,
        unit: s.unit,
        label: s.label,
        value: typeof v === "number" ? v : min,
        onChange,
      });
      break;
    }
    case "number":
      handle = uiApi.numberField({
        min: s.min,
        max: s.max,
        step: s.step,
        label: s.label,
        value: typeof v === "number" ? v : s.min ?? 0,
        onChange,
      });
      break;
    case "toggle":
      handle = uiApi.toggle({ label: s.label, checked: v === true, onChange });
      break;
    case "select": {
      const options = s.options || [];
      handle = uiApi.select({
        options,
        label: s.label,
        value: typeof v === "string" ? v : options[0]?.value ?? "",
        onChange,
      });
      break;
    }
    case "color":
      handle = uiApi.colorPicker({
        label: s.label,
        value: typeof v === "string" ? v : "#000000",
        onChange,
      });
      break;
    case "text":
      handle = uiApi.textField({
        label: s.label,
        value: typeof v === "string" ? v : "",
        onChange,
      });
      break;
    default:
      // Unknown type from a newer SDK — skip the row rather than crash the
      // whole form and take the working controls down with it.
      return null;
  }

  if (opts.onCommit) attachCommit(handle.el, s, opts.onCommit);
  return { row: wrapSettingRow(handle.el, prefix, s.help), handle };
}

/**
 * Report a value once the gesture ENDS.
 *
 * Continuous controls (slider, number, text) commit on pointerup/blur;
 * discrete ones (toggle, select, colour swatch) have no drag, so their change
 * IS the commit. Listeners are attached to the row element and die with it —
 * the control's own `destroy()` removes the subtree.
 */
function attachCommit(
  el: HTMLElement,
  s: SettingSpec,
  commit: (value: unknown) => void
): void {
  const read = () => {
    const input = el.querySelector("input, select") as
      | HTMLInputElement
      | HTMLSelectElement
      | null;
    if (!input) return undefined;
    if (s.type === "toggle") return (input as HTMLInputElement).checked;
    if (s.type === "slider" || s.type === "number") return Number(input.value);
    return input.value;
  };
  const fire = () => {
    const v = read();
    if (v !== undefined) commit(v);
  };

  if (s.type === "slider" || s.type === "number" || s.type === "text") {
    // pointerup can land outside the thumb, so listen on the row; `change`
    // covers keyboard adjustment, which never produces a pointer event.
    el.addEventListener("pointerup", fire);
    el.addEventListener("change", fire);
    el.addEventListener("blur", fire, true);
  } else {
    el.addEventListener("change", fire);
  }
}

export function wrapSettingRow(
  control: HTMLElement,
  prefix: string,
  help?: string
): HTMLElement {
  const row = document.createElement("div");
  row.className = `${prefix}-row`;
  row.appendChild(control);
  if (help) {
    const h = document.createElement("div");
    h.className = `${prefix}-help`;
    h.textContent = help;
    row.appendChild(h);
  }
  return row;
}
